import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentList from './DocumentList';  // New import
import BalanceTable from './BalanceTable';  // New import
import Navbar from './Navbar';
import './Navbar.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import DataTable from 'react-data-table-component';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

function CommitteeDetail() {
  const { acctNum } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contributors, setContributors] = useState([]);

  // Function to create custom cluster icons
  const createClusterCustomIcon = (cluster) => {
    const count = cluster.getChildCount();
    let size = 40;
    let backgroundColor = 'rgba(51, 136, 255, 0.6)';

    if (count < 10) {
      size = 30;
      backgroundColor = 'rgba(40, 167, 69, 0.6)'; // green for small
    } else if (count < 100) {
      size = 35;
      backgroundColor = 'rgba(255, 193, 7, 0.6)'; // yellow for medium
    } else {
      size = 40;
      backgroundColor = 'rgba(220, 53, 69, 0.6)'; // red for large
    }

    return new L.DivIcon({
      html: `<div style="background-color: ${backgroundColor}; border-radius: 50%; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${size/3}px; border: 2px solid white;">${count}</div>`,
      className: 'marker-cluster-custom',
      iconSize: new L.Point(size, size)
    });
  };

  // Fix Leaflet icon issue
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/committees/${acctNum}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);

        // Fetch contributors
        try {
          const contribResponse = await fetch(`/api/contributions/${acctNum}`);
          if (contribResponse.ok) {
            const contribData = await contribResponse.json();
            setContributors(contribData);
            console.log('Contributors:', contribData);
          } else {
            console.error('Failed to fetch contributors');
          }
        } catch (contribErr) {
          console.error('Error fetching contributors:', contribErr);
        }

        // Set page title using committee name
        document.title = result.Name || 'Committee Detail';
      } catch (err) {
        setError(err.message);
        document.title = 'Committee Not Found';
      } finally {
        setLoading(false);
      }
    };

    if (acctNum) fetchData();
  }, [acctNum]);

  if (loading) return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <Navbar />
      <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        Loading committee details...
      </main>
    </div>
  );

  if (error || !data) return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#000' }}>
      <Navbar />
      <main style={{ maxWidth: '900px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2>Committee Not Found</h2>
          <button onClick={() => navigate(-1)}>Back to List</button>
        </section>
      </main>
    </div>
  );

  // Compute fullChairName if chair fields exist
  const fullChairName = `${data.ChrNameFirst || ''} ${data.ChrNameMiddle ? data.ChrNameMiddle + ' ' : ''}${data.ChrNameLast || ''}`.trim();

  // Prepare fields for table (exclude _id; include computed address and chair)
  const tableData = {
    ...data,
    fullAddress: `${data.Addr1 || ''}${data.Addr2 ? ', ' + data.Addr2 : ''}, ${data.City || ''}, ${data.State || ''} ${data.Zip || ''}`.trim(),
    fullChair: fullChairName || 'N/A'
  };
  delete tableData._id;

  const fields = Object.entries(tableData)
    .filter(([key]) => !key.startsWith('ChrName') && key !== 'TrsNameFirst' && key !== 'TrsNameMiddle' && key !== 'TrsNameLast')  // Exclude raw name parts
    .map(([key, value]) => ({ key: key.replace(/([A-Z])/g, ' $1').trim(), value }));

  // DataTable columns for contributions
  const columns = [
    {
      name: 'Date',
      selector: row => row.date,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      format: row => `$${row.amount ? row.amount.toLocaleString() : 'N/A'}`,
      width: '100px',
    },
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      width: '200px',
      cell: row => row.voterId ? (
        <a 
          href={`https://www.flelections.com/donor/${row.voterId}`} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {row.name}
        </a>
      ) : row.name
    },
    {
      name: 'City',
      selector: row => row.city,
      sortable: true,
      width: '150px',
    },
    {
      name: 'State',
      selector: row => row.state,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Occupation',
      selector: row => row.occupation,
      sortable: true,
      width: '150px',
    },
  ];

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

      <main style={{ maxWidth: '1000px', margin: '18px auto', padding: '24px' }}>
        <section style={{ padding: '20px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2 style={{ marginTop: 0 }}>{data.Name}</h2>

          <table border="1" style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid black' }}>
        <tbody>
          {fields.map(({ key, value }, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ width: '30%', padding: '8px', border: '1px solid black', backgroundColor: '#f9f9f9' }}>
                <strong>{key}:</strong>
              </td>
              <td style={{ width: '70%', padding: '8px', border: '1px solid black' }}>
                {value || 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
          {/* New: Documents Table */}
          <DocumentList acctNum={acctNum} />
          <BalanceTable accountList={[{ accountName: 'Committee Account', accountNum: parseInt(acctNum) }]} />

          <div style={{ marginTop: 12 }}>
            <h3>Contributors Map</h3>
            <div style={{ height: '400px', width: '100%', position: 'relative' }}>
              <MapContainer center={[28.5383, -81.3792]} zoom={7} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MarkerClusterGroup iconCreateFunction={createClusterCustomIcon}>
                  {contributors.filter(coord => typeof coord.lat === 'number' && typeof coord.lng === 'number').map((coord, index) => (
                    <Marker key={index} position={[coord.lat, coord.lng]}>
                      <Popup>
                        <strong>{coord.name}</strong><br />
                        <hr style={{ margin: '2px 0', border: '1px solid #000' }} />
                        {coord.date}: ${coord.amount ? coord.amount.toLocaleString() : 'N/A'}
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <h3>Campaign Contributions</h3>
            <DataTable
              columns={columns}
              data={contributors}
              pagination
              paginationPerPage={25}
              defaultSortFieldId={1}
              defaultSortAsc={false}
            />
          </div>

          <button onClick={() => navigate('/committees')} style={{ marginTop: '20px' }}>Back to List</button>
        </section>
      </main>
    </div>
  );
}

export default CommitteeDetail;